import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

const TEXT = {
  es: {
    title: 'Mi cuenta',
    currentEmail: 'Email actual',
    passwordTitle: 'Cambiar contrase\u00f1a',
    passwordHint: 'M\u00ednimo 8 caracteres, con una may\u00fascula, un n\u00famero y un s\u00edmbolo.',
    currentPassword: 'Contrase\u00f1a actual',
    newPassword: 'Nueva contrase\u00f1a',
    confirmPassword: 'Repite la nueva contrase\u00f1a',
    changePassword: 'Cambiar contrase\u00f1a',
    passwordChanged: 'Contrase\u00f1a cambiada. Por seguridad, vuelve a iniciar sesi\u00f3n con la nueva contrase\u00f1a. Cerrando sesi\u00f3n...',
    mismatch: 'Las dos contrase\u00f1as nuevas no coinciden.',
    emailTitle: 'Cambiar email',
    emailHint: 'Te enviaremos un enlace al email nuevo. El cambio solo se aplica cuando lo confirmes desde all\u00ed.',
    newEmail: 'Nuevo email',
    changeEmail: 'Enviar enlace de confirmaci\u00f3n',
    emailSent: 'Te hemos enviado un enlace a {{email}}. \u00c1brelo en las pr\u00f3ximas 24 horas para confirmar el cambio (revisa tambi\u00e9n la carpeta de spam).',
    saving: 'Guardando...',
    genericError: 'No se pudo completar la operaci\u00f3n.',
  },
  en: {
    title: 'My account',
    currentEmail: 'Current email',
    passwordTitle: 'Change password',
    passwordHint: 'At least 8 characters, with an uppercase letter, a number and a symbol.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Repeat new password',
    changePassword: 'Change password',
    passwordChanged: 'Password changed. For security, please log in again with your new password. Logging out...',
    mismatch: 'The two new passwords do not match.',
    emailTitle: 'Change email',
    emailHint: 'We will send a link to the new email. The change only applies once you confirm it from there.',
    newEmail: 'New email',
    changeEmail: 'Send confirmation link',
    emailSent: 'We have sent a link to {{email}}. Open it within 24 hours to confirm the change (check your spam folder too).',
    saving: 'Saving...',
    genericError: 'The operation could not be completed.',
  },
  fr: {
    title: 'Mon compte',
    currentEmail: 'E-mail actuel',
    passwordTitle: 'Changer le mot de passe',
    passwordHint: 'Au moins 8 caract\u00e8res, avec une majuscule, un chiffre et un symbole.',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'R\u00e9p\u00e9tez le nouveau mot de passe',
    changePassword: 'Changer le mot de passe',
    passwordChanged: 'Mot de passe modifi\u00e9. Par s\u00e9curit\u00e9, reconnectez-vous avec le nouveau mot de passe. D\u00e9connexion...',
    mismatch: 'Les deux nouveaux mots de passe ne correspondent pas.',
    emailTitle: 'Changer l\u2019e-mail',
    emailHint: 'Nous enverrons un lien au nouvel e-mail. Le changement ne s\u2019applique qu\u2019apr\u00e8s confirmation depuis celui-ci.',
    newEmail: 'Nouvel e-mail',
    changeEmail: 'Envoyer le lien de confirmation',
    emailSent: 'Nous avons envoy\u00e9 un lien \u00e0 {{email}}. Ouvrez-le dans les 24 heures pour confirmer le changement (v\u00e9rifiez aussi les spams).',
    saving: 'Enregistrement...',
    genericError: 'L\u2019op\u00e9ration n\u2019a pas pu aboutir.',
  },
};

const getText = (lang) => TEXT[(lang || 'es').slice(0, 2)] || TEXT.es;

function AccountCard({ token, onAuthError }) {
  const { i18n } = useTranslation();
  const tx = getText(i18n.language);
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwState, setPwState] = useState({ busy: false, message: '', error: false });
  const [em, setEm] = useState({ newEmail: '', current: '' });
  const [emState, setEmState] = useState({ busy: false, message: '', error: false });

  useEffect(() => {
    axios
      .get(`${API_URL}/api/v1/account`, headers)
      .then((response) => setEmail(response.data.data.email || ''))
      .catch((err) => {
        if (err.response && err.response.status === 401) onAuthError();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const failure = (err, setter) => {
    if (err.response && err.response.status === 401) {
      onAuthError();
      return;
    }
    const detail = err.response && err.response.data && err.response.data.detail;
    setter({ busy: false, error: true, message: typeof detail === 'string' ? detail : tx.genericError });
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      setPwState({ busy: false, error: true, message: tx.mismatch });
      return;
    }
    setPwState({ busy: true, error: false, message: '' });
    try {
      await axios.post(`${API_URL}/api/v1/account/password`, { current_password: pw.current, new_password: pw.next }, headers);
      setPw({ current: '', next: '', confirm: '' });
      setPwState({ busy: false, error: false, message: tx.passwordChanged });
      setTimeout(() => onAuthError(), 3000);
    } catch (err) {
      failure(err, setPwState);
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmState({ busy: true, error: false, message: '' });
    try {
      await axios.post(`${API_URL}/api/v1/account/email`, { new_email: em.newEmail, current_password: em.current }, headers);
      setEmState({ busy: false, error: false, message: tx.emailSent.replace('{{email}}', em.newEmail.trim()) });
      setEm({ newEmail: '', current: '' });
    } catch (err) {
      failure(err, setEmState);
    }
  };

  const input = 'w-full bg-surface border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent';
  const label = 'text-muted text-xs block mb-1.5';
  const status = (state) =>
    state.message && <p className={`text-xs mt-3 ${state.error ? 'text-red-400' : 'text-accent'}`}>{state.message}</p>;

  return (
    <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
      <p className="text-white text-sm font-semibold mb-1">{tx.title}</p>
      <p className="text-muted text-xs">{tx.currentEmail}</p>
      <p className="text-white text-sm mb-5 break-all">{email || '\u2014'}</p>

      <form onSubmit={changePassword} className="bg-surfaceAlt border border-edge rounded-lg p-4 mb-4">
        <p className="text-white text-sm font-semibold mb-1">{tx.passwordTitle}</p>
        <p className="text-muted text-xs mb-3">{tx.passwordHint}</p>
        <div className="flex flex-col gap-3">
          <div>
            <label className={label}>{tx.currentPassword}</label>
            <input type="password" required autoComplete="current-password" value={pw.current} onChange={(e) => setPw((v) => ({ ...v, current: e.target.value }))} className={input} />
          </div>
          <div>
            <label className={label}>{tx.newPassword}</label>
            <input type="password" required autoComplete="new-password" value={pw.next} onChange={(e) => setPw((v) => ({ ...v, next: e.target.value }))} className={input} />
          </div>
          <div>
            <label className={label}>{tx.confirmPassword}</label>
            <input type="password" required autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw((v) => ({ ...v, confirm: e.target.value }))} className={input} />
          </div>
        </div>
        {status(pwState)}
        <button type="submit" disabled={pwState.busy} className="mt-4 bg-accent text-panel font-bold text-sm px-5 py-2 rounded-lg cursor-pointer">
          {pwState.busy ? tx.saving : tx.changePassword}
        </button>
      </form>

      <form onSubmit={changeEmail} className="bg-surfaceAlt border border-edge rounded-lg p-4">
        <p className="text-white text-sm font-semibold mb-1">{tx.emailTitle}</p>
        <p className="text-muted text-xs mb-3">{tx.emailHint}</p>
        <div className="flex flex-col gap-3">
          <div>
            <label className={label}>{tx.newEmail}</label>
            <input type="email" required autoComplete="email" value={em.newEmail} onChange={(e) => setEm((v) => ({ ...v, newEmail: e.target.value }))} className={input} />
          </div>
          <div>
            <label className={label}>{tx.currentPassword}</label>
            <input type="password" required autoComplete="current-password" value={em.current} onChange={(e) => setEm((v) => ({ ...v, current: e.target.value }))} className={input} />
          </div>
        </div>
        {status(emState)}
        <button type="submit" disabled={emState.busy} className="mt-4 bg-accent text-panel font-bold text-sm px-5 py-2 rounded-lg cursor-pointer">
          {emState.busy ? tx.saving : tx.changeEmail}
        </button>
      </form>
    </div>
  );
}

export default AccountCard;